import React from 'react';
import { useT } from '../../i18n/LanguageContext';
const inputClass = 'mt-1 block w-full rounded border p-2';
const methodLabel = { wire: 'Wire', ach: 'ACH', card: 'Credit card' };

export default function DistributorAccountingFields({ draft, families, methods, edit, disabled }) {
  const t = useT();
  const toggle = (group, key, enabled) =>
    edit((value) => {
      if (
        enabled &&
        group === 'familyItems' &&
        !families.some((family) => family.key === key && family.available === true)
      )
        return value;
      const entries = { ...value[group] };
      if (enabled)
        entries[key] = group === 'familyItems' ? { qbItemId: '', incomeAccountId: '' } : '';
      else delete entries[key];
      return { ...value, [group]: entries };
    });
  return (
    <fieldset disabled={disabled} className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          ['legalBuyerName', 'Exact legal buyer name', 200],
          ['qbRealmId', 'QuickBooks company ID', 80],
          ['qbCustomerId', 'QuickBooks buyer customer ID', 80],
        ].map(([key, label, length]) => (
          <label className="block" key={key}>
            {t(label)}
            <input
              className={inputClass}
              value={draft[key]}
              maxLength={length}
              autoComplete="off"
              onChange={(event) => edit((value) => ({ ...value, [key]: event.target.value }))}
            />
          </label>
        ))}
        <label className="block">
          {t('QuickBooks environment')}
          <select
            className={inputClass}
            value={draft.environment}
            onChange={(event) => edit((value) => ({ ...value, environment: event.target.value }))}
          >
            <option value="">{t('Choose environment')}</option>
            <option value="sandbox">{t('Sandbox')}</option>
            <option value="production">{t('Production')}</option>
          </select>
        </label>
      </div>
      <p>
        {t(
          'Enter IDs from the approved QuickBooks company. Saving checks the linked company; it does not create customers, items or accounts.'
        )}
      </p>
      <fieldset className="space-y-3">
        <legend className="font-medium">{t('Product-family accounting items')}</legend>
        {families.length === 0 && (
          <p>{t('No product families are available for accounting mapping.')}</p>
        )}
        {families.map((family) => (
          <div key={family.key} className="rounded border p-3 space-y-2">
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={Object.hasOwn(draft.familyItems, family.key)}
                disabled={
                  family.available === false && !Object.hasOwn(draft.familyItems, family.key)
                }
                onChange={(event) => toggle('familyItems', family.key, event.target.checked)}
              />
              {t('Map accounting for {family}', { family: family.label })}
            </label>
            {family.available === false && (
              <p>
                {t(
                  'This product family is unavailable. Remove its mapping before saving, or clear the draft mapping.'
                )}
              </p>
            )}
            {Object.hasOwn(draft.familyItems, family.key) && (
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  ['qbItemId', 'QuickBooks item ID for {family}'],
                  ['incomeAccountId', 'Income account ID for {family}'],
                ].map(([key, label]) => (
                  <label key={key} className="block">
                    {t(label, { family: family.label })}
                    <input
                      className={inputClass}
                      value={draft.familyItems[family.key][key]}
                      maxLength={80}
                      autoComplete="off"
                      onChange={(event) =>
                        edit((value) => ({
                          ...value,
                          familyItems: {
                            ...value.familyItems,
                            [family.key]: {
                              ...value.familyItems[family.key],
                              [key]: event.target.value,
                            },
                          },
                        }))
                      }
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </fieldset>
      <fieldset className="space-y-3">
        <legend className="font-medium">{t('Deposit accounts by payment method')}</legend>
        {methods.map((method) => (
          <div key={method}>
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={Object.hasOwn(draft.depositAccounts, method)}
                onChange={(event) => toggle('depositAccounts', method, event.target.checked)}
              />
              {t('Map {method} deposit account', { method: t(methodLabel[method]) })}
            </label>
            {Object.hasOwn(draft.depositAccounts, method) && (
              <label className="block">
                {t('Deposit account ID for {method}', { method: t(methodLabel[method]) })}
                <input
                  className={inputClass}
                  value={draft.depositAccounts[method]}
                  maxLength={80}
                  autoComplete="off"
                  onChange={(event) =>
                    edit((value) => ({
                      ...value,
                      depositAccounts: { ...value.depositAccounts, [method]: event.target.value },
                    }))
                  }
                />
              </label>
            )}
          </div>
        ))}
      </fieldset>
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={draft.tax.approved === true && draft.tax.code === 'NON'}
          onChange={(event) =>
            edit((value) => ({
              ...value,
              tax: { approved: event.target.checked, code: event.target.checked ? 'NON' : '' },
            }))
          }
        />
        {t('I confirm this USD sale is approved as non-taxable (NON).')}
      </label>
      <label className="block">
        {t('Accounting posting date')}
        <select
          className={inputClass}
          value={draft.postingDatePolicy}
          onChange={(event) =>
            edit((value) => ({ ...value, postingDatePolicy: event.target.value }))
          }
        >
          <option value="">{t('Choose posting date convention')}</option>
          <option value="verified_clearance_date">
            {t('Use the date in verified payment-clearance evidence')}
          </option>
        </select>
      </label>
      <p>
        {t(
          'The posting date must come from actual cleared-payment evidence. It is never inferred from today or the browser time zone.'
        )}
      </p>
    </fieldset>
  );
}
